import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeCountryStatus,
  createCountry,
  deleteCountry,
  getCountries,
  updateCountry,
} from '../api/countryApi'
import type { Country, CountryStatus, CountryUpsert, PagedResponse } from '../types'

const KEY = 'countries'

/**
 * Ülke yönetimi sunucu-durumu (026): liste + oluştur/güncelle/durum/sil/yeniden-sırala mutation'ları. Her
 * başarıda liste invalidate edilir; güncel kayıt cache'e yazılır (bayat modDate → 409 önlenir).
 * (023 useEventTypes deseniyle aynı.)
 */
export function useCountries() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: Country) =>
    qc.setQueryData<PagedResponse<Country>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((c) => (c.id === updated.id ? updated : c)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getCountries,
  })

  const create = useMutation({
    mutationFn: (body: CountryUpsert) => createCountry(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CountryUpsert }) => updateCountry(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: CountryStatus }) =>
      changeCountryStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteCountry(id),
    onSuccess: invalidate,
  })

  return { list, create, update, changeStatus, remove }
}
